import {
  type ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { cn } from '@/utils/cn';

type FadeProps = { activeKey: string; children: ReactNode; className?: string };
const MS = 250;

function useCrossfade(
  activeKey: string,
  children: ReactNode
): { displayed: ReactNode; visible: boolean } {
  const [displayed, setDisplayed] = useState(children);
  const [visible, setVisible] = useState(true);
  const prev = useRef(activeKey);
  const swap = useCallback(() => {
    setDisplayed(children);
    setVisible(true);
  }, [children]);

  useEffect(() => {
    if (activeKey === prev.current) {
      setDisplayed(children);
      return;
    }
    prev.current = activeKey;
    setVisible(false);
    const id = setTimeout(swap, MS);
    return () => clearTimeout(id);
  }, [activeKey, children, swap]);

  return { displayed, visible };
}

export function Fade({ activeKey, children, className }: FadeProps): ReactNode {
  const { displayed, visible } = useCrossfade(activeKey, children);
  return (
    <div
      className={cn(
        'transition-all ease-out',
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1',
        className
      )}
      style={{ transitionDuration: `${MS}ms` }}
    >
      {displayed}
    </div>
  );
}
